import { Controller } from '@hotwired/stimulus';
import { regionalPrices, historicalPeriod } from '../shared/regional_prices.js';
import { estimateFor, fairRange, findTrim, findState, usd } from '../shared/price_estimate.js';

// Progressive enhancement for the /prices "Price this car" island.
//
// The server renders a complete default state (national median at typical
// mileage) so the block is fully meaningful without JS — this controller only
// recomputes the estimate client-side when the visitor moves the controls.
// All inputs come from a JSON payload embedded in the page (the same
// trim/mileage/state stats the tables show). Missing state estimates fall back
// to on-demand actual listing aggregates, explicitly not adjusted for mileage.
// The math itself lives in shared/price_estimate.js, shared with
// /value-my-car so the two tools can never drift apart.
export default class extends Controller {
  static values = { url: String };

  static targets = [
    'resultLabel', 'modelNotice', 'assumptions', 'matchingLabel',
    'scopeStatus', 'payload', 'trimButton', 'miles', 'milesSlider', 'milesLabel', 'state',
    'exactMileage', 'mileageRange', 'milesFrom', 'milesTo', 'mileageBasis',
    'estimate', 'rangeLow', 'rangeHigh', 'matching', 'matchingPlace',
    'delta', 'basisCount', 'basisLabel',
  ];

  connect() {
    this.regionalResults = new Map();
    this.cfg = JSON.parse(this.payloadTarget.textContent);
    this.trimSlug = 'all';
    this.miles = this.cfg.avgMileage;
    this.stateCode = '';
    this.useMileageRange = false;
    this.fmt = usd;
    this.onRegionalData = event => {
      this.regionalResults.set(event.detail.state, event.detail.data);
      if (this.stateCode === event.detail.state) this.render();
    };
    window.addEventListener('seo:regional-data', this.onRegionalData);
    this.onEstimateRequest = () => this.render();
    window.addEventListener('seo:request-estimate', this.onEstimateRequest);
    this.onScopeRequest = () => this.publishScope();
    this.onScopeChange = event => {
      this.trimSlug = event.detail.slug || 'all';
      this.stateCode = event.detail.state || '';
      if (this.hasStateTarget) this.stateTarget.value = this.stateCode;
      this.render();
      this.publishScope();
    };
    window.addEventListener('seo:request-scope', this.onScopeRequest);
    window.addEventListener('seo:change-scope', this.onScopeChange);
    this.render();
    this.publishScope();
  }

  publishScope() {
    window.dispatchEvent(new CustomEvent('seo:scope-changed', { detail: {
      state: this.stateCode, slug: this.trimSlug || 'all',
      stateLabel: this.hasStateTarget ? this.stateTarget.selectedOptions[0]?.textContent || 'United States' : 'United States',
      trimLabel: this.trim?.label || 'All trims',
    } }));
  }

  disconnect() {
    window.removeEventListener('seo:regional-data', this.onRegionalData);
    window.removeEventListener('seo:request-estimate', this.onEstimateRequest);
    window.removeEventListener('seo:request-scope', this.onScopeRequest);
    window.removeEventListener('seo:change-scope', this.onScopeChange);
  }

  selectTrim(event) {
    this.trimSlug = event.currentTarget.dataset.trimSlug;
    // Синхронізує emphasis-криву в mileage×trim чарті нижче по сторінці.
    this.publishScope();
    this.render();
  }

  changeMiles(event) {
    this.miles = Math.min(300000, Math.max(0, Number(String(event.currentTarget.value).replace(/[,\s]/g, '')) || 0));
    this.render();
  }

  slideMiles(event) {
    this.changeMiles(event);
    if (this.hasMilesTarget) this.milesTarget.value = this.miles.toLocaleString('en-US');
  }

  formatMiles(event) {
    event.currentTarget.value = this.miles.toLocaleString('en-US');
  }

  setMiles(event) {
    this.miles = Number(event.currentTarget.dataset.miles);
    if (this.hasMilesTarget) this.milesTarget.value = this.miles;
    this.render();
  }

  toggleMileageRange(event) {
    this.useMileageRange = event.currentTarget.checked;
    this.exactMileageTarget.hidden = this.useMileageRange;
    this.mileageRangeTarget.hidden = !this.useMileageRange;
    if (this.useMileageRange) {
      this.changeMileageRange();
    } else {
      this.changeMiles({ currentTarget: this.milesTarget });
    }
  }

  changeMileageRange() {
    const parse = (value) => Math.min(300000, Math.max(0, Number(String(value).replace(/[,\s]/g, '')) || 0));
    const bounds = [parse(this.milesFromTarget.value), parse(this.milesToTarget.value)].sort((a, b) => a - b);
    [this.mileageFrom, this.mileageTo] = bounds;
    this.miles = Math.round((this.mileageFrom + this.mileageTo) / 2);
    this.render();
  }

  formatMileageRange() {
    this.changeMileageRange();
    this.milesFromTarget.value = this.mileageFrom.toLocaleString('en-US');
    this.milesToTarget.value = this.mileageTo.toLocaleString('en-US');
  }

  changeState(event) {
    this.stateCode = event.currentTarget.value;
    this.render();
    this.publishScope();
  }

  get trim() {
    return findTrim(this.cfg, this.trimSlug);
  }

  get state() {
    return findState(this.cfg, this.stateCode);
  }

  async loadRegional(code) {
    this.regionalResults.set(code, { status: 'loading' });
    try {
      const data = await regionalPrices(this.urlValue.replace('STATE', encodeURIComponent(code)));
      this.regionalResults.set(code, data);
    } catch (_) {
      this.regionalResults.set(code, { status: 'unavailable' });
    }
    if (this.stateCode === code && this.element.isConnected) this.render();
  }

  render() {
    if (this.hasMilesSliderTarget) {
      this.milesSliderTarget.value = this.miles;
      this.milesSliderTarget.setAttribute('aria-valuetext', `${this.miles.toLocaleString('en-US')} miles on the odometer`);
    }
    const trim = this.trim;
    const est = estimateFor(this.cfg, { trim: trim, miles: this.miles, state: this.state });
    const typical = estimateFor(this.cfg, { trim: trim, miles: this.cfg.avgMileage, state: null });

    this.trimButtonTargets.forEach((btn) => {
      const selected = btn.dataset.trimSlug === this.trimSlug;
      btn.classList.toggle('seo-config__seg--active', selected);
      btn.setAttribute('aria-pressed', String(selected));
    });
    const historicalResult = this.regionalResults?.get(this.stateCode)?.basis === 'historical';
    const unsupported = historicalResult || (this.stateCode && (!this.state || this.state.active === 0)) || (this.trimSlug && this.trimSlug !== 'all' && !trim);
    if (this.hasMatchingLabelTarget) this.matchingLabelTarget.textContent = 'listings';
    if (this.hasResultLabelTarget) this.resultLabelTarget.textContent = unsupported ? 'Median asking price · state listings' : 'Estimated asking price';
    if (this.hasModelNoticeTarget) this.modelNoticeTarget.hidden = Boolean(unsupported);
    if (this.hasAssumptionsTarget) this.assumptionsTarget.hidden = Boolean(unsupported);
    if (this.hasScopeStatusTarget) this.scopeStatusTarget.hidden = !unsupported;
    if (unsupported) {
      if (this.stateCode && !this.regionalResults.has(this.stateCode)) this.loadRegional(this.stateCode);
      const data = this.regionalResults.get(this.stateCode);
      const series = data?.status === 'ready' ? data.series?.find(row => row.slug === (this.trimSlug || 'all')) : null;
      const available = series?.median > 0 && series.count > 0;
      const historical = data?.basis === 'historical';
      if (historical && this.hasResultLabelTarget) this.resultLabelTarget.textContent = 'Median asking price · last 3 months';
      if (historical && this.hasMatchingLabelTarget) this.matchingLabelTarget.textContent = 'vehicles listed in the last 3 months';
      this.estimateTarget.textContent = available ? this.fmt.format(series.median) : '—';
      this.rangeLowTarget.textContent = available && series.p25 != null ? this.fmt.format(series.p25) : '—';
      this.rangeHighTarget.textContent = available && series.p75 != null ? this.fmt.format(series.p75) : '—';
      this.matchingTarget.textContent = available ? series.count.toLocaleString('en-US') : data?.status === 'empty' ? '0' : '—';
      this.matchingPlaceTarget.textContent = this.hasStateTarget ? this.stateTarget.selectedOptions[0]?.textContent || '' : '';
      if (this.hasBasisLabelTarget) this.basisLabelTarget.textContent = series?.label || trim?.label || 'All trims';
      if (this.hasMileageBasisTarget) this.mileageBasisTarget.hidden = true;
      if (this.hasScopeStatusTarget) this.scopeStatusTarget.textContent = available
        ? `${historical ? historicalPeriod(data) + ' ' : ''}Based on ${series.count.toLocaleString('en-US')} qualifying state listings${data.observed_at ? ` as of ${data.observed_at}` : ''}. Typical range is the 25th–75th percentile. ${series.count < 10 ? 'Small market sample. ' : ''}These are observed asking prices, not an estimate for your entered mileage; changing mileage does not change these statistics.`
        : data?.status === 'loading' ? 'Loading actual state listing prices…'
        : data?.status === 'unavailable' ? 'State prices are temporarily unavailable. Choose another state or All states to continue.'
        : historical && data.status === 'empty' ? 'No qualifying active listings or listings from the last 3 months in this state. Choose another state or All states.'
        : 'Not enough qualifying listings for this selection. Choose All trims, another state, or All states to see available prices.';
      // A listing median must not silently become a mileage-adjusted loan estimate.
      window.dispatchEvent(new CustomEvent('seo:estimate-changed', { detail: { price: null } }));
      return;
    }
    if (this.hasMilesLabelTarget) {
      this.milesLabelTarget.textContent = `${this.miles.toLocaleString('en-US')} mi`;
    }

    let range = fairRange(this.cfg, est, trim);
    if (this.useMileageRange) {
      const endpointRanges = [this.mileageFrom, this.mileageTo].map((miles) =>
        fairRange(this.cfg, estimateFor(this.cfg, { trim, miles, state: this.state }), trim));
      range = {
        low: Math.min(...endpointRanges.map((r) => r.low)),
        high: Math.max(...endpointRanges.map((r) => r.high)),
      };
    }
    if (this.hasMileageBasisTarget) {
      this.mileageBasisTarget.hidden = !this.useMileageRange;
      this.mileageBasisTarget.textContent = this.useMileageRange
        ? `For ${this.mileageFrom.toLocaleString('en-US')}–${this.mileageTo.toLocaleString('en-US')} miles. Main estimate and loan calculation use the midpoint (${this.miles.toLocaleString('en-US')} mi); the price range covers both endpoints. This does not filter the listings below.`
        : '';
    }
    this.estimateTarget.textContent = this.fmt.format(est);
    this.rangeLowTarget.textContent = this.fmt.format(range.low);
    this.rangeHighTarget.textContent = this.fmt.format(range.high);

    // The payment calculator on the same page prefills from this estimate.
    window.dispatchEvent(new CustomEvent('seo:estimate-changed', { detail: { price: est } }));

    const deltaPct = Math.round((est / typical - 1) * 100);
    // "typical" is the SELECTED version at typical mileage, so the chip reads
    // as mileage+location effect, never as the trim gap itself.
    this.deltaTarget.textContent = `${deltaPct >= 0 ? '+' : ''}${deltaPct}% vs this version at typical mileage`;

    const matching = this.state ? this.state.active : (trim ? trim.count : this.cfg.listingCount);
    this.matchingTarget.textContent = matching.toLocaleString('en-US');
    this.matchingPlaceTarget.textContent = this.state ? `${this.state.name} · all trims (estimate uses the selected trim)` : 'the U.S.';

    if (this.hasBasisCountTarget) {
      this.basisCountTarget.textContent =
        (trim ? trim.count : this.cfg.listingCount).toLocaleString('en-US');
    }
    if (this.hasBasisLabelTarget) {
      this.basisLabelTarget.textContent = trim?.label || 'All trims';
    }
  }
}
